import React, { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import './Home.css';
import {
  FaBell,
  FaChalkboardTeacher,
  FaSearch,
  FaBookOpen,
  FaShoppingCart,
  FaHeart,
  FaUserEdit,
  FaCog,
  FaCommentDots,
  FaSignOutAlt,
  FaHome,
  FaCertificate,
  FaGift,
  FaInfoCircle,
} from 'react-icons/fa';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import logo from '../../assets/logo.png'; // Import your logo image here
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/Footer/Footer';
import Sponsors from '../../components/Sponsors/Sponsors';
import api from '../../api';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake } from '@fortawesome/free-solid-svg-icons';
import CourseSuggestion from '../../components/CourseSuggestion/CourseSuggestion';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import AIAssistant from '../../components/AIAssistant/AIAssistant';
import WalkingAnimation from '../../components/WalkingAnimation/WalkingAnimation';
import { useNotifications } from '../../context/NotificationContext';
import { useCart } from '../../context/CartContext';
import Translate from '../../components/Translate/Translate';


const Home = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const { logout, profile, profileLoading, isLoggedIn, token } = useAuth();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('Learner');
  const [profilePicture, setProfilePicture] = useState();
  const [courses, setCourses] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  // const [showCategories, setShowCategories] = useState(false); 
  const dropdownRef = useRef(null);
  const { notificationCount, notifications, markAsRead } = useNotifications();
  const { cartCount } = useCart();
  // const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [statusChecked, setStatusChecked] = useState(false);
  const typingEffectRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [typingFinished, setTypingFinished] = useState(false); // to store when typing ends
  const [searchHistory, setSearchHistory] = useState([]); // Add state for search history

  const [activeCategory, setActiveCategory] = useState(null); // State to track the active category
  const [subcategoryMenuPosition, setSubcategoryMenuPosition] = useState({ top: 0, left: 0 }); // State for subcategory menu position
  const subcategoryRef = useRef(null); // Ref for the subcategory menu

  const [showAltCategories, setShowAltCategories] = useState(false); // NEW: State for toggling alt categories

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    if (!profileLoading && profile) {
      const name = profile.name || 'Learner';
      setUserName(name);
      setProfilePicture(
        getProfilePictureUrl(profile.profilePicture, api.img, defaultProfilePic, name),
      );
      // Set search history from profile
      setSearchHistory(profile.searchHistory || []);
    }
  }, [profile, profileLoading]);

  // ✅ Check Instructor Submission Status
  const checkInstructorStatus = async () => {
    if (!isLoggedIn || profileLoading) return; // ✅ Wait until profile is loaded

    if (!profile || !profile._id) {
      toast.error('Please log in before accessing the instructor section.');
      return;
    }

    try {
      const response = await axios.get(api.checkInstructorSubmissionStatus(profile._id)); // ✅ Use correct `profile._id`

      if (response.data.status === 'filled') {
        navigate('/dashboard'); // ✅ Redirect to Dashboard if criteria filled
      } else {
        navigate('/instructor-landing'); // ✅ Redirect to Instructor Landing Page if not filled
      }
    } catch (error) {
      console.error('Error checking instructor status:', error);
      toast.error('There was an issue verifying your instructor status. Please try again.');
    }
  };
  const [categories, setCategories] = useState([]); // Initialize as an empty array


  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(api.getAllCategories, {
          headers: {
            Authorization: `Bearer ${token}`, // Pass the token here
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();

        // Ensure `categories` key exists and is an array
        if (data.categories && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          console.error('Unexpected data format:', data);
          setCategories([]); // Set to an empty array if the response format is unexpected
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]); // Set to an empty array in case of error
      }
    };

    if (token) {
      fetchCategories(); // Only fetch if a token is available
    }
  }, [token]);

  const handleMouseEnter = (category, event) => {
    setActiveCategory(category); // Set the active category on mouse enter
    const { top, left, height } = event.currentTarget.getBoundingClientRect(); // Get the position of the main-category
    const subcategoryWidth = 205; // Set this to the width of your subcategory-menu
    const rightEdge = left + subcategoryWidth; // Calculate the right edge of the subcategory-menu

    // Check if the right edge exceeds the viewport width
    const viewportWidth = window.innerWidth;
    const adjustedLeft = rightEdge > viewportWidth ? left - subcategoryWidth + event.currentTarget.offsetWidth : left;

    setSubcategoryMenuPosition({ top: top + height, left: adjustedLeft }); // Set the position of the subcategory-menu
  };

  const handleMouseLeave = (event) => {
    // Only clear the active category if the mouse is not over the subcategory menu
    if (
      !subcategoryRef.current ||
      !event.relatedTarget ||
      !(event.relatedTarget instanceof Node) || // Check if relatedTarget is a Node
      !subcategoryRef.current.contains(event.relatedTarget)
    ) {
      setActiveCategory(null); // Clear the active category on mouse leave
    }
  };


  // useEffect(() => {
  //   if (!statusChecked) {
  //     checkInstructorStatus();
  //   }
  // }, [profile, isLoggedIn, profileLoading]); // ✅ Runs when profile changes

  const TypingEffect = ({ onComplete }) => {
    const [text, setText] = useState('');
    const fullText = 'Type the course name, topic, or category you\'re looking for.';
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
      let currentIndex = 0;
      const intervalId = setInterval(() => {
        if (currentIndex < fullText.length) {
          setText(fullText.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(intervalId);
          setIsComplete(true);
          if (onComplete) {
            onComplete();
          }
        }
      }, 50);

      return () => {
        clearInterval(intervalId);
      };
    }, []);

    return (
      <div className="search-card">
        <img
          src={require('../../assets/searchavatar.png')}
          alt="Search"
          className="search-card-image"
        />
        <div className="search-card-content">
          <p>{text}</p>
        </div>
      </div>
    );
  };




  useEffect(() => {
    // Close dropdown if clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };


  // ✅ Handle Instructor Button Click
  const handleInstructorClick = async () => {
    checkInstructorStatus();
  };

  // ✅ Fetch Search Results
  const fetchSearchResults = async (query) => {
    try {
      const response = await axios.get(`${api.getAllCourses}?search=${query}`);
      setSearchResults(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  };
  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideSearch);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideSearch);
    };
  }, []);

  // ✅ Debounce API Calls for Search
  const debouncedFetchResults = useCallback(
    debounce((query) => {
      if (query.trim()) {
        fetchSearchResults(query);
      } else {
        setSearchResults([]);
      }
    }, 300),
    [],
  );

  // Update the search query and fetch results when it changes
  useEffect(() => {
    debouncedFetchResults(searchQuery);
    return () => {
      debouncedFetchResults.cancel();
    };
  }, [searchQuery, debouncedFetchResults]);

  // Function to update search history
  const updateSearchHistory = async (searchTerm) => {
    if (!isLoggedIn || !token) return; // Only save history for logged-in users

    try {
      await axios.post(api.updateSearchHistory,
        { searchTerm },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error('Error updating search history:', error);
    }
  };

  // Add resize listener for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchQuery.trim()) {
        updateSearchHistory(searchQuery.trim());
        navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(searchQuery)}`);
        if (isMobile) {
          setShowSearchOverlay(false);
        }
      }
    }
  };

  return (
    <div className="home-container">
      <header className="homenavbar">
        <img
          src={logo}
          alt="UlsaApp Logo"
          className="homelogo-img"
        />

        {/* Beta button placed next to the logo */}
        <button
          className="beta-button"
          onClick={() => navigate('/beta')}
          title="Beta"
        >
          Beta Version
        </button>


        {/* Desktop Search */}
        {!isMobile && (
          <div ref={searchWrapperRef} className="users-home-homesearch-wrapper">
            <form
              className="users-home-homesearch-bar"
              onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  updateSearchHistory(searchQuery.trim());
                  navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(searchQuery)}`);
                }
              }}
            >
              <input
                className="homesearch-input"
                type="text"
                placeholder="Search for courses, topics, or resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                onClick={() => {
                  setShowSuggestions(true);
                  if (!typingEffectRef.current && !typingFinished) {
                    typingEffectRef.current = (
                      <TypingEffect onComplete={() => setTypingFinished(true)} />
                    );
                  }
                }}
              />
              <button type="submit" className="homesearch-icon">
                <FaSearch title="Search" />
              </button>
            </form>

            {showSuggestions && (
              <div className="search-suggestions">
                {typingEffectRef.current}
                {searchHistory.length > 0 && (
                  <div className="search-history-section">
                    <h4 className="search-history-title">Recent Searches</h4>
                    {searchHistory.slice(0, 3).map((term, index) => (
                      <div
                        key={index}
                        className="search-history-item"
                        onClick={() => {
                          setSearchQuery(term);
                          updateSearchHistory(term);
                          navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(term)}`);
                        }}
                      >
                        <FaSearch className="search-history-icon" />
                        <span>{term}</span>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.length > 0 ? (
                  searchResults.map((course) => (
                    <div
                      key={course._id}
                      className="search-suggestion-item"
                      onClick={() => {
                        updateSearchHistory(course.title);
                        navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(course.title)}`);
                      }}
                    >
                      <h4>{course.title}</h4>
                    </div>
                  ))
                ) : (
                  <div className="no-msg">No results found....</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Mobile Search Icon */}
        {isMobile && (
          <button
            className="homesearch-icon"
            onClick={() => setShowSearchOverlay(true)}
          >
            <FaSearch title="Search" />
          </button>
        )}

        {/* Mobile Search Overlay */}
        {isMobile && (
          <div className={`search-overlay ${showSearchOverlay ? 'active' : ''}`}>
            <div className="search-overlay-header">
              <button
                className="search-overlay-close"
                onClick={() => setShowSearchOverlay(false)}
              >
                ✕
              </button>
            </div>
            <div className="users-home-homesearch-wrapper">
              <form
                className="users-home-homesearch-bar"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    updateSearchHistory(searchQuery.trim());
                    navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(searchQuery)}`);
                    setShowSearchOverlay(false);
                  }
                }}
              >
                <input
                  className="homesearch-input"
                  type="text"
                  placeholder="Search for courses, topics, or resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  onClick={() => {
                    setShowSuggestions(true);
                    if (!typingEffectRef.current && !typingFinished) {
                      typingEffectRef.current = (
                        <TypingEffect onComplete={() => setTypingFinished(true)} />
                      );
                    }
                  }}
                  autoFocus
                />
              </form>

              {showSuggestions && (
                <div className="search-suggestions">
                  {typingEffectRef.current}
                  {searchHistory.length > 0 && (
                    <div className="search-history-section">
                      <h4 className="search-history-title">Recent Searches</h4>
                      {searchHistory.slice(0, 3).map((term, index) => (
                        <div
                          key={index}
                          className="search-history-item"
                          onClick={() => {
                            setSearchQuery(term);
                            updateSearchHistory(term);
                            navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(term)}`);
                            setShowSearchOverlay(false);
                          }}
                        >
                          <FaSearch className="search-history-icon" />
                          <span>{term}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {searchResults.length > 0 ? (
                    searchResults.map((course) => (
                      <div
                        key={course._id}
                        className="search-suggestion-item"
                        onClick={() => {
                          updateSearchHistory(course.title);
                          navigate(`/category/all-courses/all-courses?search=${encodeURIComponent(course.title)}`);
                          setShowSearchOverlay(false);
                        }}
                      >
                        <h4>{course.title}</h4>
                      </div>
                    ))
                  ) : (
                    <div className="no-msg">No results found....</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        
        <button className="instructor-button" onClick={handleInstructorClick}>
          Instructor
        </button>

        {/* Language selector */}
        <Translate />

        <div className="account-subscription-wrapper navbar-account">
          <button
            className="account-button"
            onClick={() => navigate('/subscriptions')}
            type="button"
          >
            Subscription
          </button>
        </div>


        {/* <button
          className="menu-icon-list"
          onClick={() => {
            setShowCategories(!showCategories);
            setShowAltCategories(!showAltCategories); // Toggle alt categories
          }}
        >
          {showCategories ? "✖" : <FaBars title="Menu" />}
        </button> */}



        <div className="navbar-icons">
          {/* <FaHome
            className="home-icon"
            title="Home"
            onClick={() => navigate('/')}
          /> */}
          <FaBookOpen
            className={`icon ${window.location.pathname === '/learning' ? 'active' : ''}`}
            title="My Learning"
            onClick={() => navigate('/learning')}
          />
          <FaHeart
            className={`icon ${window.location.pathname === '/wishlist' ? 'active' : ''}`}
            title="Wishlist"
            onClick={() => navigate('/wishlist')}
          />
          <div className="cart-icon-container">
            <FaShoppingCart
              className={`icon ${window.location.pathname === '/cart' ? 'active' : ''}`}
              title="Cart"
              onClick={() => navigate('/cart')}
            />
            {cartCount > 0 && (
              <span className="cart-badge">{cartCount}</span>
            )}
          </div>
          <FaChalkboardTeacher
            className="instructor-alt"
            title="Instructor"
            onClick={handleInstructorClick} />

          <div className="notification-icon-container">
          <FaBell
            className="icon"
            title="Notifications"
            onClick={() => {
              navigate('/notifications');
            }}
          />
          {notificationCount > 0 && (
            <span className="home-notification-badge">{notificationCount}</span>
          )}
        </div>
        </div>

        <div
          className="profile-icon-container"
          // title="Profile"
          ref={dropdownRef}
        >
          <img
            src={profilePicture}
            alt="profile picture"
            className="profile-icon"
            onClick={() => setShowDropdown(!showDropdown)}
          />
          {showDropdown && (
            <div className="dropdown-menu">
              <div className="profile-info">
                <img
                  src={profilePicture}
                  alt="profile picture"
                  className="dropdown-profile-icon"
                />
                <p className="homw-dropdown-item">{userName}</p>
              </div>

              <div className="account-subscription-wrapper profile-account-subscription">
                <button
                  className="account-button menu-item profile-subscription-button"
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/subscriptions');
                  }}
                  type="button"
                >
                  Subscription
                </button>
              </div>

              <button
                className="menu-item"
                onClick={() => navigate('/edit-profile')}
              >
                <FaUserEdit className="menu-item-icon" /> Edit Profile
              </button> 
              <button
                className="menu-item"
                onClick={() => navigate('/account-settings')}
              >
                <FaCog className="menu-item-icon" /> Account Settings
              </button>
              <button
                className="menu-item"
                onClick={() => navigate('/users-feedback')}
              >
                <FaCommentDots className="menu-item-icon" /> Feedback
              </button>
              {/* <button
                className="menu-item"
                onClick={() => navigate('/dashboard')}
              >
                <FaChalkboardTeacher className="menu-item-icon" /> Instructor
              </button> */}
              <button
                className="menu-item"
                onClick={() => navigate('/my-certificates')}
              >
                <FaCertificate className="menu-item-icon" /> My Certificates
              </button>
              <button
                className="menu-item"
                onClick={() => navigate('/gifted')}
              >
                <FaGift className="menu-item-icon" /> Gift Management
              </button>
              <button
                className="menu-item"
                onClick={() => navigate('/about')}
              >
                <FaInfoCircle className="menu-item-icon" /> About Us
              </button>
              {/* <button
                className="menu-item"
                onClick={() => navigate("/about")}
              >
                <FaInfoCircle className="menu-item-icon" /> About Us
              </button> */}
              <button className="logout-button" onClick={handleLogout}>
                <FaSignOutAlt className="menu-item-icon" /> Logout
              </button>
            </div>
          )}
        </div>

      </header>

      <div className="menu-hide">
        {/* Main Categories Container */}
        <div className="home-categories-container">
          {Array.isArray(categories) && categories.length > 0 ? (
            categories.map((category) => (
              <div key={category._id} className="home-category-item">
                <button
                  className="main-category"
                  onMouseEnter={(event) => handleMouseEnter(category.name, event)}
                  onMouseLeave={handleMouseLeave}
                >
                  {category.name}
                </button>
                {subcategoryMenuPosition && activeCategory === category.name && (
                  <div
                    className="subcategory-menu"
                    style={{
                      top: `${subcategoryMenuPosition.top}px`,
                      left: `${subcategoryMenuPosition.left}px`,
                      position: 'absolute', // Ensure it's positioned absolutely
                    }}
                    ref={subcategoryRef}
                    onMouseEnter={() => setActiveCategory(category.name)} // Keep the category active when hovering over the subcategory menu
                    onMouseLeave={handleMouseLeave} // Clear the active category when leaving the subcategory menu
                  >
                    {category.subcategories.map((subcategory) => (
                      <button
                        key={subcategory._id || subcategory.name}
                        className="subcategory-item"
                        onClick={() => navigate(`/category/${category.name}/${subcategory.name}`)}
                      >
                        {subcategory.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p>No categories available.</p>
          )}
        </div>

        {/* Subcategory menu should be placed here, outside of the home-categories-container */}
        <div className="subcategory-menu">
          {/* Render subcategories here based on the selected category */}
        </div>


      </div>


      <div className="home-content">
        <h1 className="welcomeicon">
          {' '}
          Welcome {userName}{' '}
          <FontAwesomeIcon icon={faHandshake} style={{ color: 'white' }} />
        </h1>
        <p>Shape your future with UlsaApp...</p>
        <div className="home-cta-buttons">
          {/* <button
            className="university-btn"
            onClick={async () => {
              try {
                if (!isLoggedIn || !profile?._id) {
                  toast.error("Please log in to access university programs");
                  return;
                }

                const url = api.checkUniversityCriteriaStatus(profile._id);
                // console.log('Making request to:', url);
                // console.log('With token:', token ? 'Token present' : 'No token');

                const response = await axios.get(url, {
                  headers: { Authorization: `Bearer ${token}` }
                });

                //console.log('University criteria check response:', response.data);

                if (response.data.success) {
                  const { hasSubmitted, status } = response.data.data;

                  //console.log('hasSubmitted:', hasSubmitted, 'status:', status);

                  // Check if user exists in university criteria table and status is 'submitted'
                  if (hasSubmitted && status === 'submitted') {
                    //console.log('Redirecting to university-home');
                    navigate('/university-home');
                  } else {
                    //console.log('Redirecting to university');
                    navigate('/university');
                  }
                } else {
                  //console.log('Response not successful, redirecting to university');
                  navigate('/university');
                }
              } catch (error) {
                //console.error('Error checking university criteria status:', error);
                navigate('/university');
              }
            }}
          >
            <FaUniversity /> Explore University Programs
          </button> */}

        </div>
        <WalkingAnimation />
      </div>

      

      <CourseSuggestion />
      {/* <Sponsors /> */}
      <Footer />
      <AIAssistant />
    </div>
  );
};

export default Home;
